import { AuthSignInSystemType, AuthSystem, IAuthenticationAnalyticsContext, IAuthenticationContext } from '@pressreader/authentication-types';
import { error } from '@pressreader/logger';
import { getErrorResourceKey, localizedStringWithDefault } from '@pressreader/resources';
import { apiSiteSignIn, apiSiteSignOut, svcAuth } from '@pressreader/services';
import { LocalizableError } from '@pressreader/types';
import { executeReCaptcha, getErrorMessage, ReCaptchaVersion } from '@pressreader/utils';

import { apiCheckRegistrationStatus, apiRecoverPassword, apiRegister, apiSendVerificationEmail, apiSignIn, apiSignOut } from './auth.api';
import {
    publishAccountChangedEvent,
    sendLoginBeganEvent,
    sendLoginDoneEvent,
    sendLoginFailedEvent,
    sendLogOutBeganEvent,
    sendLogOutDoneEvent,
    sendLogOutFailedEvent,
    sendSignUpBeganEvent,
    sendSignUpDoneEvent,
    sendSignUpFailedEvent,
} from './auth.events';
import { AuthFailureStage, describeAuthFailure, stageOfAuthorization } from './auth.failure';
import { createEmailExternalProfile } from './external.profile.api';
import { AuthStatus, NewUser, SignInResultDto } from './types';
import { isUserAuthenticated } from './user.api';

const authErrorResources = {
    [AuthStatus.InvalidUserNameOrPassword]: 'Dialogs.Signin.InvalidLoginOrPass',
    [AuthStatus.ExceedMaxSimultaneousSessionsPerUser]: 'Dialogs.Signin.MaxAllowedSimultaneousSessionsExceeded',
    [AuthStatus.CaptchaFailed]: 'ReCaptcha.Verification.ErrorMessage',
    [AuthStatus.UnknownError]: 'Errors.UnknownError',
    [AuthStatus.ExceedMaxIncorrectPasswordAttempts]: 'Auth.Errors.ExceedMaxIncorrectPasswordAttempts',
    [AuthStatus.UserIsAlreadyRegistered]: 'Auth.Errors.UserIsAlreadyRegistered',
    [AuthStatus.InvalidEmailAddress]: 'Auth.Errors.InvalidEmailAddress',
    [AuthStatus.MaxNumberOfEmailsReached]: 'Auth.Errors.MaxNumberOfEmailsReached',
    [AuthStatus.UserIsPendingVerification]: 'Auth.Errors.UserIsAlreadyPendingVerification',
};

interface AuthUserRequest {
    signInResult: SignInResultDto;
    enableAutologin: boolean;
    authContext?: IAuthenticationContext;
}

/**
 * The library a sign-up carries after a library card sign-in. Callers pass the reference object even when there
 * was no such sign-in, only with empty fields, so its presence alone says nothing.
 */
function toLibraryUserRef(libraryUser: unknown): { libraryId?: number; provider?: string } {
    const { library, provider } = (libraryUser ?? {}) as { library?: unknown; provider?: unknown };
    return {
        libraryId: Number(library) || undefined,
        provider: typeof provider === 'string' && provider ? provider : undefined,
    };
}

async function authorizeUser({ signInResult, enableAutologin, authContext }: AuthUserRequest): Promise<void> {
    if (!signInResult) {
        throw new Error('signInResult not specified');
    }

    if (signInResult.Status !== AuthStatus.Success) {
        // Carry the key alongside the wording: this is a verdict on the credentials, and a caller
        // that has to tell a verdict from a transport failure (toSignInFailure) reads the key.
        // An unmapped status would otherwise localize `undefined` and throw.
        const resourceKey = authErrorResources[signInResult.Status] ?? authErrorResources[AuthStatus.UnknownError];
        throw new LocalizableError(localizedStringWithDefault(resourceKey, 'Unknown Error'), resourceKey);
    }
    const oldUserKey = svcAuth.userKey();
    const siteSignInResult = await apiSiteSignIn(signInResult.Token, enableAutologin);
    svcAuth.updateAuthorizationTickets(siteSignInResult);
    if (!isUserAuthenticated()) {
        throw new Error('Not authorized');
    }

    // account change
    if (svcAuth.userKey() !== oldUserKey) {
        publishAccountChangedEvent('before', authContext);
    }
    // account remains the same
    publishAccountChangedEvent('after', authContext);
}

async function signIn({
    userName,
    password,
    enableAutologin,
    analyticsContext,
    authProcessState,
}: {
    userName: string;
    password: string;
    enableAutologin: boolean;
    analyticsContext?: IAuthenticationAnalyticsContext;
    authProcessState?: string;
}) {
    let stage: AuthFailureStage = 'captcha';
    try {
        sendLoginBeganEvent({ system: 'email' });

        const reCaptchaToken = await executeReCaptcha('signin');
        stage = 'request';
        const signInResult = await apiSignIn({ userName, password, reCaptchaVersion: ReCaptchaVersion.v3, reCaptchaToken, authProcessState });
        stage = stageOfAuthorization(signInResult);
        await authorizeUser({
            signInResult,
            enableAutologin,
            authContext: { method: 'signin', system: 'email', analytics: analyticsContext },
        });

        sendLoginDoneEvent({ system: 'email', analyticsCtx: analyticsContext });
    } catch (e: unknown) {
        sendLoginFailedEvent({
            error: { message: getErrorMessage(e), resourceKey: getErrorResourceKey(e) },
            system: 'email',
            failure: describeAuthFailure(e, stage, 'sign_in'),
            analyticsCtx: analyticsContext,
        });
        throw e;
    }
}

async function signInWithSignInResult(
    signInResult: SignInResultDto,
    {
        system,
        enableAutologin,
        analyticsContext,
    }: {
        system: AuthSystem;
        enableAutologin: boolean;
        analyticsContext?: IAuthenticationAnalyticsContext;
    },
) {
    try {
        sendLoginBeganEvent({ system });
        await authorizeUser({
            signInResult,
            enableAutologin,
            authContext: { method: 'signin', system, analytics: analyticsContext },
        });
        sendLoginDoneEvent({ system, analyticsCtx: analyticsContext });
    } catch (e: unknown) {
        sendLoginFailedEvent({
            error: { message: getErrorMessage(e), resourceKey: getErrorResourceKey(e) },
            system,
            failure: describeAuthFailure(e, stageOfAuthorization(signInResult), 'sign_in'),
            analyticsCtx: analyticsContext,
        });
        throw e;
    }
}

async function signUp(
    newUser: NewUser,
    analyticsContext?: IAuthenticationAnalyticsContext,
    register: (newUser: NewUser) => Promise<SignInResultDto> = apiRegister,
): Promise<{
    sponsorId?: number;
    pendingVerification?: boolean;
    email?: string;
    password?: string;
}> {
    const { libraryId, provider } = toLibraryUserRef(newUser.libraryUser);
    const system: AuthSystem = libraryId ? 'library' : 'email';
    let stage: AuthFailureStage = 'captcha';
    try {
        sendSignUpBeganEvent({ system: 'email' });

        newUser.reCaptchaVersion = ReCaptchaVersion.v3;
        newUser.reCaptchaToken = await executeReCaptcha('signup');

        analyticsContext = {
            ...analyticsContext,
            consentToMarketingEmails: newUser.enablePromotional || newUser.enableService,
        };

        stage = 'request';
        const signInResult = await register(newUser);

        analyticsContext.verificationRequired = !!signInResult.SponsorId;
        const authContext: IAuthenticationContext = {
            method: 'signup',
            system: newUser.libraryUser ? 'library' : 'email',
            analytics: analyticsContext,
        };

        if (signInResult.Status !== AuthStatus.UserIsPendingVerification) {
            stage = stageOfAuthorization(signInResult);
            await authorizeUser({
                signInResult,
                authContext,
                enableAutologin: false,
            });
        }

        if (signInResult.SponsorId) {
            stage = 'profile';
            await createEmailExternalProfile(newUser.emailAddress, signInResult.SponsorId);
        }

        sendSignUpDoneEvent({
            system,
            provider,
            libraryId,
            sponsorId: signInResult.SponsorId,
            analyticsCtx: analyticsContext,
            emailValidationRequired: signInResult.Status === AuthStatus.UserIsPendingVerification,
            email: signInResult.Status === AuthStatus.UserIsPendingVerification ? newUser.emailAddress : undefined,
        });
        return {
            sponsorId: signInResult.SponsorId,
            pendingVerification: signInResult.Status === AuthStatus.UserIsPendingVerification,
            email: newUser.emailAddress,
            password: newUser.password,
        };
    } catch (e: unknown) {
        sendSignUpFailedEvent({
            error: { message: getErrorMessage(e), resourceKey: getErrorResourceKey(e), system: AuthSignInSystemType.Email },
            system,
            provider,
            libraryId,
            failure: describeAuthFailure(e, stage, 'sign_up'),
            analyticsCtx: analyticsContext,
        });
        throw e;
    }
}

async function signOut({ supressReauthorize }: { supressReauthorize?: boolean } = {}) {
    try {
        sendLogOutBeganEvent();

        await apiSignOut();
        const siteSignOutResult = await apiSiteSignOut();
        svcAuth.updateAuthorizationTickets(siteSignOutResult);
        if (supressReauthorize) {
            return;
        }

        publishAccountChangedEvent('before');
        publishAccountChangedEvent('after');

        sendLogOutDoneEvent();
    } catch (e) {
        sendLogOutFailedEvent({ error: { message: getErrorMessage(e), system: AuthSignInSystemType.Email } });
        throw e;
    }
}

async function recoverPassword({ userName }: { userName: string }) {
    try {
        await apiRecoverPassword(userName);
    } catch (e) {
        error(e);
        throw e;
    }
}

async function resendEmailVerification(emailAddress: string) {
    try {
        return await apiSendVerificationEmail(emailAddress);
    } catch (e) {
        error(e);
        throw e;
    }
}

async function checkRegistrationStatus(emailAddress: string) {
    try {
        return await apiCheckRegistrationStatus(emailAddress);
    } catch (e) {
        error(e);
        throw e;
    }
}

export { authorizeUser, checkRegistrationStatus, recoverPassword, resendEmailVerification, signIn, signInWithSignInResult, signOut, signUp };
